import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Volume2, VolumeX } from 'lucide-react';
import { PageBar, SearchInput, ToolButton } from '../../components/admin/dash/Toolbar';
import { SessionList } from '../../components/admin/livechat/SessionList';
import { ConversationPanel } from '../../components/admin/livechat/ConversationPanel';
import { VisitorPanel } from '../../components/admin/livechat/VisitorPanel';
import { useLiveChat } from '../../components/admin/livechat/useLiveChat';
import { ChatSession, StatusFilter } from '../../components/admin/livechat/types';
import { cn } from '../../lib/utils';

export default function AdminLiveChat() {
  const {
    sessions,
    loading,
    refreshing,
    refresh,
    selectedSession,
    messages,
    selectSession,
    soundEnabled,
    toggleSound,
    takingOver,
    handleTakeover,
    sendingMessage,
    newMessage,
    setNewMessage,
    handleSendMessage,
    closing,
    handleCloseSession,
  } = useLiveChat();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [mobileView, setMobileView] = useState<'list' | 'conversation'>('list');

  useEffect(() => {
    if (!selectedSession) setMobileView('list');
  }, [selectedSession]);

  const counts = useMemo(() => {
    const waiting = sessions.filter((s) => s.status === 'waiting_for_admin').length;
    const withTeam = sessions.filter((s) => s.status === 'admin_joined').length;
    return { waiting, withTeam, ai: sessions.length - waiting - withTeam };
  }, [sessions]);

  const handleSelect = (session: ChatSession) => {
    selectSession(session);
    setMobileView('conversation');
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-[color:var(--oa-card)] font-poppins text-[color:var(--oa-muted)]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[color:var(--oa-card)] font-poppins text-[color:var(--oa-text)] antialiased">
      <PageBar
        title="Live chat"
        meta={
          <span className="tabular-nums">
            {counts.waiting} waiting · {counts.withTeam} with team · {counts.ai} with AI
          </span>
        }
        actions={
          <>
            <ToolButton active={soundEnabled} onClick={toggleSound} title={soundEnabled ? 'Sound alerts on' : 'Sound alerts off'}>
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </ToolButton>
            <ToolButton onClick={refresh} disabled={refreshing} title="Refresh">
              <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </ToolButton>
          </>
        }
      >
        <SearchInput value={search} onChange={setSearch} placeholder="Search visitor, email or message" />
      </PageBar>

      <div className="flex min-h-0 flex-1">
        <div className={cn('w-full shrink-0 flex-col border-[color:var(--oa-line-2)] md:flex md:w-[340px] md:border-r', mobileView === 'list' ? 'flex' : 'hidden')}>
          <SessionList
            sessions={sessions}
            search={search}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            selectedId={selectedSession?.id ?? null}
            onSelect={handleSelect}
          />
        </div>

        <div className={cn('min-w-0 flex-1 flex-col', mobileView === 'conversation' ? 'flex' : 'hidden md:flex')}>
          <ConversationPanel
            session={selectedSession}
            messages={messages}
            onBack={() => setMobileView('list')}
            onTakeover={() => handleTakeover()}
            takingOver={takingOver}
            onClose={handleCloseSession}
            closing={closing}
            newMessage={newMessage}
            onNewMessageChange={setNewMessage}
            onSend={handleSendMessage}
            sendingMessage={sendingMessage}
          />
        </div>

        {selectedSession ? (
          <div className="hidden w-[280px] shrink-0 border-l border-[color:var(--oa-line-2)] xl:flex xl:flex-col">
            <VisitorPanel session={selectedSession} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
